/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import { h, JSX, VNode } from "preact";
import { CSSProperties, useEffect, useRef, useState } from "preact/compat";
import { useTranslationContext } from "../context/translation.js";
import { doAutoFocus } from "./utils.js";

export type ButtonProps = Omit<
  JSX.ButtonHTMLAttributes<HTMLButtonElement>,
  "type"
> & {
  submit?: boolean;
  focus?: boolean;
  type?: "button" | "submit" | "reset";
};

/** A plain button.  Notifications and asynchronous state belong to callers. */
export function Button({ focus, submit, type, ...rest }: ButtonProps): VNode {
  return (
    <button
      {...rest}
      ref={focus ? doAutoFocus : undefined}
      type={type ?? (submit ? "submit" : "button")}
    />
  );
}

export type AsyncButtonProps = Omit<ButtonProps, "onClick"> & {
  onClick?: (
    event: JSX.TargetedMouseEvent<HTMLButtonElement>,
  ) => void | Promise<void>;
};

/** A button with local progress UI for an asynchronous click handler. */
export function AsyncButton({
  children,
  disabled,
  onClick,
  ...rest
}: AsyncButtonProps): VNode {
  const [running, setRunning] = useState(false);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  async function handleClick(
    event: JSX.TargetedMouseEvent<HTMLButtonElement>,
  ): Promise<void> {
    if (!onClick || running) return;
    setRunning(true);
    try {
      await onClick(event);
    } catch (error) {
      // AsyncButton deliberately has no notification dependency.  Callers
      // that need user-facing errors should use useNotifiedOperation.
      console.error("Unhandled asynchronous button action", error);
    } finally {
      if (mounted.current) setRunning(false);
    }
  }

  return (
    <Button
      {...rest}
      aria-busy={running}
      disabled={disabled || !onClick || running}
      onClick={handleClick}
    >
      <span style={{ position: "relative" }}>
        <span style={{ visibility: running ? "hidden" : undefined }}>
          {children}
        </span>
        {running ? <Wait /> : undefined}
      </span>
    </Button>
  );
}

const animateSpin: CSSProperties = {
  animation: "spin 1s cubic-bezier(0.5, 0, 0.5, 1) infinite",
};
const size24: CSSProperties = { width: 24, height: 24 };
const screenReaderOnly: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};
const centered: CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
};

function Wait(): VNode {
  const { i18n } = useTranslationContext();
  return (
    <span role="status" style={{ ...centered, ...size24 }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        style={{ height: "1lh", ...animateSpin }}
      >
        <path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
      </svg>
      <span style={screenReaderOnly}>
        <i18n.Translate>Loading</i18n.Translate>
      </span>
    </span>
  );
}
